import React from 'react';
import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { ReportStyleSpec } from '../../services/rxfService';

export const DEFAULT_REPORT_STYLE: ReportStyleSpec = {
  schema_version: 1,
  report_length: 'detailed',
  impression_style: 'numbered_synthesis',
  rec_style: 'standard',
  bold_positives: false,
  max_impression_points: 0,
  section_order: ['exam', 'clinical_history', 'comparison', 'technique', 'findings', 'impression'],
  hidden_optional_sections: [],
  heading_style: 'uppercase',
  blank_line_policy: 'single',
  normal_finding_policy: 'full',
  include_remaining_normal_findings: true,
  findings_layout: 'paragraphs',
  sentence_style: 'complete',
  terminology_style: 'standard',
  measurement_style: 'source',
  emphasis_style: 'none',
  impression_inclusion_policy: 'all_relevant',
  anatomical_detail_style: 'group_related',
  custom_instructions: '',
  formatting_example: '',
};

interface Props {
  value: ReportStyleSpec;
  onChange: (value: ReportStyleSpec) => void;
  disabled?: boolean;
  error?: string | null;
}

const ReportStyleBuilder: React.FC<Props> = ({ value, onChange, disabled = false, error }) => {
  const set = <K extends keyof ReportStyleSpec>(key: K, next: ReportStyleSpec[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Report length</InputLabel>
        <Select value={value.report_length} label="Report length" onChange={(e) => set('report_length', e.target.value as ReportStyleSpec['report_length'])}>
          <MenuItem value="concise">Concise</MenuItem>
          <MenuItem value="detailed">Detailed</MenuItem>
          <MenuItem value="comprehensive">Comprehensive</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id="impression-presentation-label">Impression presentation</InputLabel>
        <Select labelId="impression-presentation-label" value={value.impression_style} label="Impression presentation" onChange={(e) => set('impression_style', e.target.value as ReportStyleSpec['impression_style'])}>
          <MenuItem value="numbered_synthesis">Numbered synthesis</MenuItem>
          <MenuItem value="brief_bullets">Brief bullets</MenuItem>
          <MenuItem value="narrative">Narrative prose</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id="impression-content-label">Impression content</InputLabel>
        <Select labelId="impression-content-label" value={value.impression_inclusion_policy} label="Impression content" onChange={(e) => set('impression_inclusion_policy', e.target.value as ReportStyleSpec['impression_inclusion_policy'])}>
          <MenuItem value="all_relevant">All clinically relevant findings</MenuItem>
          <MenuItem value="positive_only">Positive findings only</MenuItem>
        </Select>
      </FormControl>
      {value.impression_inclusion_policy === 'positive_only' && (
        <Alert severity="info">
          Routine normal or negative statements are omitted from the impression. Explicitly dictated critical negatives and safety-critical exclusions remain.
        </Alert>
      )}

      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id="anatomical-levels-label">Anatomical levels</InputLabel>
        <Select labelId="anatomical-levels-label" value={value.anatomical_detail_style} label="Anatomical levels" onChange={(e) => set('anatomical_detail_style', e.target.value as ReportStyleSpec['anatomical_detail_style'])}>
          <MenuItem value="group_related">Group related levels</MenuItem>
          <MenuItem value="independent_levels">Describe every level independently</MenuItem>
        </Select>
      </FormControl>
      {value.anatomical_detail_style === 'independent_levels' && (
        <Alert severity="info">
          Only levels present in the transcript or required template are separated. Missing observations are never invented.
        </Alert>
      )}

      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Findings layout</InputLabel>
        <Select value={value.findings_layout} label="Findings layout" onChange={(e) => set('findings_layout', e.target.value as ReportStyleSpec['findings_layout'])}>
          <MenuItem value="paragraphs">Paragraphs</MenuItem>
          <MenuItem value="bullets">Bullets</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={<Switch checked={value.include_remaining_normal_findings} onChange={(e) => set('include_remaining_normal_findings', e.target.checked)} disabled={disabled} />}
        label="Include remaining normal findings"
      />
      <Typography variant="caption" color="text.secondary">
        Cover remaining structures within the acquired exam. Unspecified observations are identified as not described, not confirmed normal. Review all additions before approval. Turning this off preserves dictated findings only.
      </Typography>
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Normal findings</InputLabel>
        <Select value={value.normal_finding_policy} label="Normal findings" onChange={(e) => set('normal_finding_policy', e.target.value as ReportStyleSpec['normal_finding_policy'])}>
          <MenuItem value="full">Describe fully</MenuItem>
          <MenuItem value="condensed">Condense</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={<Switch checked={value.emphasis_style === 'bold_abnormal'} disabled={disabled} onChange={(e) => onChange({ ...value, bold_positives: e.target.checked, emphasis_style: e.target.checked ? 'bold_abnormal' : 'none' })} />}
        label="Bold abnormal findings"
      />

      <TextField
        size="small"
        type="number"
        label="Maximum impression points (0 = unlimited)"
        value={value.max_impression_points}
        disabled={disabled}
        inputProps={{ min: 0, max: 10 }}
        onChange={(e) => set('max_impression_points', Math.max(0, Math.min(10, Number(e.target.value))))}
      />

      <TextField
        label="Additional formatting guidance"
        value={value.custom_instructions || ''}
        disabled={disabled}
        multiline
        minRows={3}
        inputProps={{ maxLength: 1000 }}
        onChange={(e) => set('custom_instructions', e.target.value)}
        helperText={`${(value.custom_instructions || '').length}/1000. Formatting only; clinical-content changes are rejected.`}
      />

      <TextField
        label="Formatting example (optional)"
        value={value.formatting_example || ''}
        disabled={disabled}
        multiline
        minRows={3}
        inputProps={{ maxLength: 1500 }}
        onChange={(e) => set('formatting_example', e.target.value)}
        helperText="Only presentation is copied; clinical content in the example is ignored."
      />

      <Typography variant="caption" color="text.secondary">
        Required EXAM, TECHNIQUE, FINDINGS, and IMPRESSION sections remain locked by clinical policy.
      </Typography>
    </Stack>
  );
};

export default ReportStyleBuilder;
